import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { Table, Button, Tag, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faTriangleExclamation, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import AnalyticsPage from '../../shared-components/analytics/AnalyticsPage';
import QualityNote from '../../shared-components/analytics/QualityNote';
import StatTile from '../../shared-components/analytics/StatTile';
import BarList from '../../shared-components/charts/BarList';
import { getSalesHierarchy } from '../../../analytics/analyticsApi';
import { thousand, toCurrency } from '../../../functions';

// marginPct is null (not 0) when there is no revenue. 0% reads as "we made
// nothing"; the truth is "there is nothing to divide by".
const pct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

function AnalyticsMargin() {
  const { t } = useTranslation();
  const [level, setLevel] = useState(1);       // hierarchy: Grouping/Department/Category
  const [slicer, setSlicer] = useState(null);  // Division / Item Group — NOT levels
  const [drill, setDrill] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const groupBy = slicer ?? level;

  useEffect(() => {
    setLoading(true);
    getSalesHierarchy({ groupBy, categoryParent: drill?.key })
      .then((d) => { setResult(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [groupBy, drill]);

  const rows = result?.items ?? [];
  const totals = result?.totals;

  return (
    <AnalyticsPage
      title={t('analytics_margin_title')}
      subtitle={t('analytics_margin_subtitle')}
    >
      {/* KPI row is bound to `totals` (whole filtered set), never to the page. */}
      <div className="mb-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          label={t('analytics_net_sales')}
          value={toCurrency(totals?.netSalesAmount)}
          emphasis
          hint={t('analytics_totals_help')}
        />
        <StatTile
          label={t('analytics_gross_profit')}
          value={toCurrency(totals?.grossProfitAmount)}
        />
        <StatTile
          label={t('analytics_margin_pct')}
          value={pct(totals?.marginPct)}
          hint={t('analytics_margin_help')}
        />
        <StatTile
          label={t('analytics_sales_lines')}
          value={thousand(totals?.salesLineCount)}
        />
      </div>

      {/* The two structures are drawn differently on purpose. The hierarchy is a
          path with chevrons because Grouping really does contain Department
          which really does contain Category. Division and Item Group are
          standalone chips behind a divider because they are flat attributes on
          different source tables with no parent -- drawing them as a fourth and
          fifth step would imply a containment that does not exist. */}
      <div className="mb-5 rounded-card border border-line bg-surface p-4 shadow-card">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-7">
          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted">
              {t('analytics_hierarchy')}
              <Tooltip styles={{ root: { maxWidth: 'none' } }} title={t('analytics_hierarchy_help')}>
                <span className="group cursor-help">
                  <FontAwesomeIcon icon={faCircleInfo} className="text-gold-400" style={{ fontSize: '0.7rem' }} />
                </span>
              </Tooltip>
            </div>
            <div className="flex flex-wrap items-center gap-1">
              {[
                { label: t('analytics_grouping'), value: 0 },
                { label: t('analytics_department'), value: 1 },
                { label: t('analytics_category'), value: 2 },
              ].map((lv, i) => (
                <React.Fragment key={lv.value}>
                  {i > 0 && (
                    <FontAwesomeIcon
                      icon={faChevronRight}
                      className="mx-0.5 text-gray-300"
                      style={{ fontSize: '0.6rem' }}
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => { setSlicer(null); setLevel(lv.value); setDrill(null); }}
                    className={`${slicer == null && level === lv.value
                      ? 'bg-gold-100 text-gold-800 font-semibold'
                      : 'text-sidebarText hover:bg-gold-50'} rounded-lg px-3 py-1.5 text-sm transition-colors duration-150 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400`}
                  >
                    {lv.label}
                  </button>
                </React.Fragment>
              ))}
            </div>
            <div className="mt-2 text-xs text-muted">
              {drill ? (
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span>
                    {t('analytics_showing_within')}{' '}
                    <span className="font-semibold text-ink">{drill.name}</span>
                  </span>
                  <Button size="small" onClick={() => { setDrill(null); setLevel(1); }}>
                    {t('analytics_clear')}
                  </Button>
                </span>
              ) : (
                slicer == null && level === 1 && t('analytics_drill_hint')
              )}
            </div>
          </div>

          <div className="hidden w-px self-stretch bg-line lg:block" />
          <div className="h-px w-full bg-line lg:hidden" />

          <div className="min-w-0">
            <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted">
              {t('analytics_flat_attributes')}
              <Tooltip styles={{ root: { maxWidth: 'none' } }} title={t('analytics_slicer_help')}>
                <span className="group cursor-help">
                  <FontAwesomeIcon icon={faCircleInfo} className="text-gold-400" style={{ fontSize: '0.7rem' }} />
                </span>
              </Tooltip>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { label: t('analytics_division'), value: 3 },
                { label: t('analytics_item_group'), value: 4 },
              ].map((f) => (
                <button
                  key={f.value}
                  type="button"
                  onClick={() => {
                    setDrill(null);
                    setSlicer(slicer === f.value ? null : f.value);
                  }}
                  className={`${slicer === f.value
                    ? 'border-gold-500 bg-gold-100 text-gold-800 font-semibold'
                    : 'border-line text-sidebarText hover:bg-gold-50'} rounded-lg border px-3 py-1.5 text-sm transition-colors duration-150 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-400`}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <div className="mt-2 text-xs text-muted">{t('analytics_flat_hint')}</div>
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="rounded-card border border-line bg-surface p-4 shadow-card">
          <div className="mb-3 text-sm font-semibold text-ink">{t('analytics_net_sales_by')}</div>
          <BarList
            rows={_.map(rows.slice(0, 12), (r) => ({
              id: r.key,
              label: r.name,
              value: r.netSalesAmount,
            }))}
            formatValue={(v) => toCurrency(v)}
            renderTooltip={(row) => {
              const full = _.find(rows, (r) => r.key === row.id);
              return (
                <div className="text-xs tabular-nums" style={{ minWidth: '13rem' }}>
                  <div className="mb-1 font-semibold">{full?.name}</div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_net_sales')}</span>
                    <span>{toCurrency(full?.netSalesAmount)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_gross_profit')}</span>
                    <span>{toCurrency(full?.grossProfitAmount)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_margin_pct')}</span>
                    <span>{pct(full?.marginPct)}</span>
                  </div>
                  <div className="flex justify-between gap-4">
                    <span className="opacity-80">{t('analytics_share')}</span>
                    <span>{pct(full?.shareOfTotalPct)}</span>
                  </div>
                </div>
              );
            }}
          />
        </div>

        <div className="rounded-card border border-line bg-surface p-4 shadow-card">
          <Table
            size="small"
            bordered={false}
            loading={loading}
            rowKey="key"
            pagination={false}
            scroll={{ x: 'max-content' }}
            dataSource={rows}
            locale={{ emptyText: t('no_data') }}
            onRow={(row) => ({
              onClick: () => {
                if (slicer == null && level === 1 && row.netSalesAmount > 0) {
                  setDrill({ key: row.key, name: row.name });
                  setLevel(2);
                }
              },
              className: slicer == null && level === 1 ? 'cursor-pointer' : '',
            })}
            columns={[
              {
                title: t('name'),
                key: 'name',
                render: (r) => (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-sm font-semibold text-ink">{r.name}</span>
                    {r.hasUnmappedClassification && (
                      <Tag color="warning" className="m-0">{t('analytics_unmapped')}</Tag>
                    )}
                  </span>
                ),
              },
              {
                title: t('analytics_net_sales'),
                key: 'net',
                align: 'right',
                render: (r) => <span className="tabular-nums">{toCurrency(r.netSalesAmount)}</span>,
              },
              {
                title: t('analytics_gross_profit'),
                key: 'profit',
                align: 'right',
                render: (r) => <span className="tabular-nums">{toCurrency(r.grossProfitAmount)}</span>,
              },
              {
                title: t('analytics_margin_pct'),
                key: 'margin',
                align: 'right',
                render: (r) => (
                  <span className="inline-flex items-center gap-1.5 tabular-nums font-semibold text-ink">
                    {pct(r.marginPct)}
                    {r.zeroCostSalesAmount > 0 && (
                      <Tooltip
                        styles={{ root: { maxWidth: 'none' } }}
                        title={`${t('analytics_margin_overstated')} ${toCurrency(r.zeroCostSalesAmount)}`}
                      >
                        <span className="group cursor-help">
                          <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className="text-warning"
                            style={{ fontSize: '0.7rem' }}
                          />
                        </span>
                      </Tooltip>
                    )}
                  </span>
                ),
              },
              {
                title: t('analytics_share'),
                key: 'share',
                align: 'right',
                render: (r) => <span className="tabular-nums text-muted">{pct(r.shareOfTotalPct)}</span>,
              },
            ]}
            summary={() => (
              <Table.Summary.Row className="bg-gold-50 font-semibold">
                <Table.Summary.Cell index={0}>{t('total')}</Table.Summary.Cell>
                <Table.Summary.Cell index={1} align="right">
                  <span className="tabular-nums">{toCurrency(totals?.netSalesAmount)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <span className="tabular-nums">{toCurrency(totals?.grossProfitAmount)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} align="right">
                  <span className="tabular-nums">{pct(totals?.marginPct)}</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4} align="right">100.0%</Table.Summary.Cell>
              </Table.Summary.Row>
            )}
          />
          <QualityNote quality={result?.quality} className="mt-4 border-t border-line pt-3" />
          <p className="mt-3 text-xs leading-relaxed text-muted">
            {t('analytics_as_was_note')}
          </p>
        </div>
      </div>
    </AnalyticsPage>
  );
}

export default AnalyticsMargin;
